<template>
  <div class="row align-middle">
    <div class="text-center mw-100 px-5" style="margin: auto" v-if="isMobile">
      <i class="fa fa-mobile-phone fa-rotate-90 fa-3x"></i>
      <br />
      <p class="font-weight-500">
        This section is best viewed in landscape mode
      </p>
    </div>

    <div class="col-12">
      <p class="lead" style="letter-spacing: -0.5px">
        This tool allows a clear, visual comparison of the future readiness of
        up to four cities by means of a radar chart. You can compare cities
        across the overall CFRI score and eight key pillars of urban future
        readiness.
      </p>
    </div>

    <div class="col-12 text-center animate__animated animate__fadeIn mb-3 mt-3">
      <p class="font-weight-500 help-text-green">
        <i class="fa fa-lightbulb-o mr-2" />Select up to four cities to compare
        their future readiness scores across different metrics
      </p>
    </div>

    <!-- Metric Selection Controls -->
    <div class="col-12 mb-4">
      <div class="card">
        <div class="card-header">
          <h6 class="mb-0">Select Comparison Metrics</h6>
        </div>
        <div class="card-body">
          <div class="row">
            <div class="col-12">
              <div class="row">
                <div
                  class="col-md-4 col-sm-6"
                  v-for="pillar in mainPillars"
                  :key="pillar.key"
                >
                  <div class="custom-control custom-checkbox mb-2">
                    <input
                      type="checkbox"
                      class="custom-control-input"
                      :id="`pillar-${pillar.key}`"
                      v-model="pillar.visible"
                      @change="updateChart"
                    />
                    <label
                      class="custom-control-label text-sm"
                      :for="`pillar-${pillar.key}`"
                    >
                      {{ pillar.label }}
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="col-12">
      <div class="radar-container">
        <RadarComparison :chartData="this.chartData"></RadarComparison>
      </div>
    </div>

    <div class="col-12 pb-5">
      <div class="row">
        <div class="col-3 text-center">
          <h5 style="border-bottom: solid #65cbda">City A</h5>
          <b-form-select
            size="md"
            v-model="cityA"
            :options="cityOptions"
            class="small-select"
          ></b-form-select>
        </div>
        <div class="col-3 text-center">
          <h5 style="border-bottom: solid #8635d5">City B</h5>
          <b-form-select
            size="md"
            v-model="cityB"
            :options="cityOptions"
            class="small-select"
          ></b-form-select>
        </div>
        <div class="col-3 text-center">
          <h5 style="border-bottom: solid #f24982">City C</h5>
          <b-form-select
            size="md"
            v-model="cityC"
            :options="cityOptions"
            class="small-select"
          ></b-form-select>
        </div>
        <div class="col-3 text-center">
          <h5 style="border-bottom: solid #2dc574">City D</h5>
          <b-form-select
            size="md"
            v-model="cityD"
            :options="cityOptions"
            class="small-select"
          ></b-form-select>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import RadarComparison from "@/components-2025/charts/RadarComparison";
import citiesData from "@/data_2025/cities.json";

export default {
  name: "CityComparison",
  components: { RadarComparison },
  data() {
    return {
      citiesData: citiesData,
      cityA: "",
      cityB: "",
      cityC: "",
      cityD: "",

      /**
       * Main pillars - some visible by default for performance
       */
      mainPillars: [
        {
          key: "PeopleCentricity",
          field: "people_centricity_score",
          label: "People Centricity",
          visible: true,
          color: "#DB4437",
        },
        {
          key: "Openness",
          field: "openness_score",
          label: "Openness",
          visible: true,
          color: "#F4B400",
        },
        {
          key: "Sustainability",
          field: "sustainability_score",
          label: "Sustainability",
          visible: true,
          color: "#0F9D58",
        },
        {
          key: "InstitutionsGovernance",
          field: "institutions_governance_score",
          label: "Institutions & Governance",
          visible: false,
          color: "#9C27B0",
        },
        {
          key: "TransportInfrastructure",
          field: "transport_infrastructure_score",
          label: "Transport & Infrastructure",
          visible: false,
          color: "#009688",
        },
        {
          key: "Innovation",
          field: "innovation_score",
          label: "Innovation",
          visible: true,
          color: "#FF9800",
        },
        {
          key: "Vibrancy",
          field: "vibrancy_score",
          label: "Vibrancy",
          visible: false,
          color: "#E91E63",
        },
        {
          key: "EIntelligence",
          field: "e_intelligence_score",
          label: "E-Readiness",
          visible: true,
          color: "#3F51B5",
        },
      ],
    };
  },

  computed: {
    cityOptions() {
      const cities = Object.values(this.citiesData)
        .sort((a, b) => a.city.localeCompare(b.city))
        .map((city) => ({
          text: `${city.city}, ${city.country}`,
          value: city.slug,
        }));

      return [{ text: "Select a city", value: "" }, ...cities];
    },

    selectedCities() {
      const colors = ["#65CBDA", "#8635D5", "#F24982", "#2DC574"];
      const selectedCities = [];

      [this.cityA, this.cityB, this.cityC, this.cityD].forEach(
        (citySlug, index) => {
          if (citySlug && this.citiesData[citySlug]) {
            selectedCities.push({
              city: this.citiesData[citySlug],
              borderColor: colors[index],
            });
          }
        }
      );

      return selectedCities;
    },

    visibleMetrics() {
      return this.mainPillars.filter((metric) => metric.visible);
    },

    chartData() {
      const labels = this.visibleMetrics.map((metric) => metric.label);

      const datasets = this.selectedCities.map(({ city, borderColor }) => ({
        label: city.city,
        data: this.visibleMetrics.map((metric) => {
          const value = city[metric.field];
          return value ? parseFloat(value.toFixed(2)) : 0;
        }),
        fill: true,
        lineTension: 0.2,
        borderColor: borderColor,
        backgroundColor: borderColor + "20",
        borderWidth: 3,
      }));

      return {
        labels: labels,
        datasets: datasets,
      };
    },

    isMobile() {
      return screen.width <= 760;
    },
  },

  methods: {
    updateChart() {
      this.$forceUpdate();
    },
  },

  mounted() {
    const defaultPillars = [
      "PeopleCentricity",
      "Openness",
      "Sustainability",
      "Innovation",
      "EIntelligence",
    ];
    this.mainPillars.forEach((pillar) => {
      pillar.visible = defaultPillars.includes(pillar.key);
    });
  },
};
</script>

<style scoped>
.radar-container {
  max-width: 600px;
  margin: auto;
}

.help-text-green {
  color: #0f9d58;
}

.text-sm {
  font-size: 0.875rem;
}

.custom-control-label {
  cursor: pointer;
}

.card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.small-select {
  font-size: 0.8rem;
}

.small-select option {
  font-size: 0.8rem;
}
</style>
